import { c as _c } from "react/compiler-runtime";
import { getChatSessionsByChatSessionIdOptions } from '@onefootprint/fetch-dashboard';
import { IcoChevronRightDouble16, IcoClose16, IcoFloating16, IcoFolders16, IcoFullscreen16, IcoMinusSmall16, IcoSidebar16, IcoSidebarLeftClosed16 } from '@onefootprint/icons';
import { Dropdown, IconButton } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useReducedMotion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useMobileNavigation from '@/components/layout/hooks/use-mobile-navigation';
import { useChatSessionIsLive } from '@/features/ask-percy';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useSession from '@/hooks/use-session';
import useApprovalRequiredNotification from '../../hooks/use-approval-required-notification';
import useChatWidgetEnabled from '../../hooks/use-chat-widget-enabled';
import useChatWidgetLayout from '../../hooks/use-chat-widget-layout';
import useChatWidgetLayoutSwitch from '../../hooks/use-chat-widget-layout-switch';
import useChatWidgetMorph from '../../hooks/use-chat-widget-morph';
import useChatWidgetOpen, { useChatWidgetShortcut } from '../../hooks/use-chat-widget-open';
import useChatWidgetPosition, { POSITION_ANCHOR } from '../../hooks/use-chat-widget-position';
import useChatWidgetPresentation from '../../hooks/use-chat-widget-presentation';
import useDocumentsPanelOpen from '../../hooks/use-documents-panel-open';
import useInvestigationDock, { useInvestigationDockRelease } from '../../hooks/use-investigation-dock';
import useMobileChatNavigation from '../../hooks/use-mobile-chat-navigation';
import useFollowPercyOpens from '../../hooks/use-open-in-dashboard';
import { useResourceArrivals } from '../../hooks/use-resource-arrivals';
import useResourceContext from '../../hooks/use-resource-context';
import useRightDrawerInset from '../../hooks/use-right-drawer-inset';
import useTurnCompleteNotification from '../../hooks/use-turn-complete-notification';
import { resolveNavigationChatSessionId } from '../../utils/resolve-navigation-chat-session-id';
import { presentResource, resourceKey } from '../../utils/resource-presentation';
import ChatHistoryDropdown from '../chat-history-dropdown';
import ChatShareControl from '../chat-share-control';
import OpenChatButton from '../open-chat-button';
import BackgroundAgentsPanel from './background-agents-panel';
import { useChatResources } from './chat-resources-context';
import ChatWidgetDocumentsDrawer from './chat-widget-documents-drawer';
import ChatWidgetMorphLayer from './chat-widget-morph-layer';
import { useChatWidgetSlot } from './chat-widget-slot-context';
import ContextExplorer from './context-explorer';
import { useInvestigationScope } from './investigation-scope-context';
import NotificationsHeaderButton from './notifications-header-button';
import useChatWidgetSession from './use-chat-widget-session';
const ChatWidgetBody = dynamic(() => import('./chat-widget-body'), {
  ssr: false
});
const DOCUMENTS_PANEL_WIDTH = 318;

// Anchor (bottom/right/etc.) is applied per-position via POSITION_ANCHOR + --percy-gap
// in the host effect below, so the floating card follows the launch button's corner.
const floatingCardClassName = 'fixed z-20 flex flex-col w-[472px] h-[640px] bg-primary border border-tertiary rounded-2xl shadow-lg overflow-hidden';

// Docked rounded card; its inset comes from the slot's padding (see
// chat-widget-slot.tsx). rounded-lg matches the main content card it sits beside
// (the morph layer measures the live radius, so the floating↔docked morph still interpolates).
const sidebarClassName = 'flex flex-col h-full w-full bg-primary border border-tertiary rounded-lg overflow-hidden';

// Fullscreen fills the slot that overlays the content card (see ChatWidgetFullscreenSlot),
// so the browser keeps it sized to the content area with no nav-width sync. The content
// card supplies the border, edit-mode color, and rounding; the radius here is only so the
// body-portaled morph overlay (z-40, not clipped by the card) keeps rounded corners
// while animating in. It is the card's radius less its border (1.5px at most, in edit mode):
// at the card's own radius, the slot's white shows in the corners behind the home's backdrop.
// On a phone it is a square sheet over the page instead: the shadow marks its top edge while
// it slides up, and the slot clips it once it arrives.
const fullscreenClassName = 'relative flex flex-col h-full w-full bg-primary rounded-[calc(var(--radius-lg)-1.5px)] overflow-hidden max-md:rounded-none max-md:shadow-[0_-8px_24px_rgb(0_0_0/0.12)]';

// 'button' (closed) reuses the floating box; it's hidden via `hostClosed`.
const hostClassNameByPresentation = {
  fullscreen: fullscreenClassName,
  docked: sidebarClassName,
  floating: floatingCardClassName,
  button: floatingCardClassName
} as const;

// iOS's sheet curve: a quick start that settles softly. Leaving is quicker than arriving.
const SHEET_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const SHEET_ENTER_MS = 400;
const SHEET_EXIT_MS = 260;
const SHEET_FADE_MS = 150;

// Web Animations run the slide on the compositor while the chat mounts underneath.
const slideSheet = (el: HTMLElement, direction: 'in' | 'out', reduceMotion: boolean, previous: Animation | null): Animation | null => {
  if (typeof el.animate !== 'function') return null;
  // Read before cancelling, so an exit that interrupts the entrance leaves from where the sheet is.
  const {
    transform,
    opacity
  } = getComputedStyle(el);
  previous?.cancel();
  const shown: Keyframe = reduceMotion ? {
    opacity: 1
  } : {
    transform: 'translateY(0)'
  };
  const hidden: Keyframe = reduceMotion ? {
    opacity: 0
  } : {
    transform: 'translateY(100%)'
  };
  const current: Keyframe = reduceMotion ? {
    opacity
  } : {
    transform: transform === 'none' ? 'translateY(0)' : transform
  };
  return el.animate(direction === 'in' ? [hidden, shown] : [current, hidden], {
    duration: reduceMotion ? SHEET_FADE_MS : direction === 'in' ? SHEET_ENTER_MS : SHEET_EXIT_MS,
    easing: reduceMotion ? 'ease-out' : SHEET_EASE,
    // The exit holds the sheet off-screen until the close lands and hides the host; without the
    // fill the chat would flash back for a frame in between.
    fill: direction === 'out' ? 'forwards' : 'none'
  });
};
const LAUNCHER_EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
const LAUNCHER_ENTER_MS = 200;
const revealLauncher = (el: HTMLElement | null, reduceMotion: boolean) => {
  if (typeof el?.animate !== 'function') return;
  el.animate(reduceMotion ? [{
    opacity: 0
  }, {
    opacity: 1
  }] : [{
    opacity: 0,
    transform: 'scale(0.9)'
  }, {
    opacity: 1,
    transform: 'none'
  }], {
    duration: LAUNCHER_ENTER_MS,
    easing: LAUNCHER_EASE
  });
};
const iconButtonClass = 'flex items-center justify-center size-11 md:size-7 rounded-full hover:bg-hover-control cursor-pointer focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';

// Knock a notch out of the folder icon where the count badge sits, so the badge reads as punching
// through the icon rather than pasting flat on top of it. Masking the icon (not drawing a ring)
// keeps this independent of the button's background, which shifts on hover / when the panel is open.
const badgeCutout: CSSProperties = {
  WebkitMaskImage: 'radial-gradient(circle at calc(100% + 2px) -2px, transparent 10px, #000 10.5px)',
  maskImage: 'radial-gradient(circle at calc(100% + 2px) -2px, transparent 10px, #000 10.5px)'
};

// One persistent host node for the chat. Moving this node between containers
// (vs. re-rendering into different trees) avoids remounting ChatExperience, so
// its queries don't refire on every layout switch.
const useHostEl = () => {
  const [host] = useState(_temp);
  return host;
};
type ChatWidgetShareControlProps = {
  chatSessionId: string;
  userId?: string;
};
const ChatWidgetShareControl = t0 => {
  const $ = _c(11);
  const {
    chatSessionId,
    userId
  } = t0;
  const isLive = useChatSessionIsLive(chatSessionId);
  let t1;
  if ($[0] !== chatSessionId || $[1] !== isLive) {
    t1 = getChatSessionsByChatSessionIdOptions({
      path: {
        chatSessionId
      },
      headers: isLive === undefined ? undefined : {
        "x-is-live": String(isLive)
      }
    });
    $[0] = chatSessionId;
    $[1] = isLive;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = isLive !== undefined;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      ...t1,
      enabled: t2,
      retry: false,
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      placeholderData: _temp2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const sessionQuery = useQuery(t3);
  const session = sessionQuery.data;
  if (!session) {
    return null;
  }
  const t4 = session.tenantUserId === userId;
  let t5;
  if ($[6] !== chatSessionId || $[7] !== isLive || $[8] !== session.isShared || $[9] !== t4) {
    t5 = <ChatShareControl chatSessionId={chatSessionId} isShared={session.isShared} isOwner={t4} isLive={isLive} />;
    $[6] = chatSessionId;
    $[7] = isLive;
    $[8] = session.isShared;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};

/** Global Percy chat: a floating card, or a docked sidebar / full-screen panel (each portaled into a layout slot). */
const ChatWidget = () => {
  const enabled = useChatWidgetEnabled();
  const isMobile = useMobileLayout();
  const {
    isOpen: navigationOpen,
    setOpen: setNavigationOpen
  } = useMobileNavigation();
  const {
    t: tCommon
  } = useTranslation('common');
  const {
    t
  } = useTranslation('agents', {
    keyPrefix: 'floatingChat'
  });
  // Separate copilot translator for presentResource (it reads full `chat.resources.*` keys).
  const {
    t: tCopilot
  } = useTranslation('copilot');
  const {
    isOpen,
    open,
    close
  } = useChatWidgetOpen();
  const reduceMotion = useReducedMotion() ?? false;
  const sheetRef = useRef<Animation | null>(null);
  const sheetEnterRef = useRef(false);
  const sheetClosingRef = useRef(false);
  const handleTurnComplete = useTurnCompleteNotification();
  const handleApprovalRequired = useApprovalRequiredNotification();
  useChatWidgetShortcut();
  useMobileChatNavigation();
  const {
    mode,
    isSidebar,
    isFullscreen
  } = useChatWidgetLayout();
  const switchLayout = useChatWidgetLayoutSwitch();
  const {
    position,
    setPosition
  } = useChatWidgetPosition();
  const {
    slotEl,
    fullscreenSlotEl
  } = useChatWidgetSlot();
  const scope = useInvestigationScope();
  // Investigation exception: dock as a sidebar (non-persisted) while the page carries a
  // `?chat=` — an arrival default the user releases with an explicit layout/collapse pick.
  const lockedSidebar = useInvestigationDock();
  const releaseDock = useInvestigationDockRelease();
  const {
    data: {
      user,
      org
    }
  } = useSession();
  const documentsPanel = useDocumentsPanelOpen();
  // The folder button owns the Resources mode; the Background-agents mode is opened elsewhere (the
  // composer indicator), so the button reads active only when the panel is showing Resources.
  const resourcesOpen = documentsPanel.isOpen && documentsPanel.tab === 'resources';
  const rightDrawer = useRightDrawerInset();
  const {
    items
  } = useChatResources();
  const router = useRouter();
  // The row to highlight: the resource the current route is on. useResourceContext maps the route to
  // the active resource per kind (entity, onboarding via onboarding_id, investigation, agent) — its
  // `resourceKey` is the explorer row's key. An artifact is viewed within its investigation via
  // `?artifact=`, so prefer that id when present.
  const activeResource = useResourceContext();
  const activeResourceId = router.query.artifact as string | undefined ?? (activeResource ? resourceKey(activeResource) : undefined);
  // The docked host's rect — the documents overlay is positioned flush to its
  // left edge (it floats over the page, doesn't take the host's flow width).
  const [hostRect, setHostRect] = useState<DOMRect | null>(null);
  // Fly a newly-created resource's marker to the toggle, then pulse the count badge.
  const documentsToggleRef = useRef<HTMLButtonElement>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  useResourceArrivals(documentsToggleRef, badgeRef);
  useFollowPercyOpens();

  // Session + route logic (which chat is active, deep-link seeding, the adopt/new-chat handlers).
  const {
    sessionId,
    isAssumingRouteTenant,
    lockedFullscreen,
    adoptSession,
    handleNewChat
  } = useChatWidgetSession({
    enabled,
    scope,
    orgId: org?.id
  });

  // "Presenting" = the user's persisted open flag OR a route that owns the chat (shell →
  // fullscreen, investigation dock → sidebar). Route-implied open isn't persisted, so a refresh
  // on such a route can't strand the widget open on the next page.
  const effectiveOpen = isOpen || lockedFullscreen || lockedSidebar && !isMobile;
  const activeChatSessionId = sessionId ?? undefined;
  const hostEl = useHostEl();

  // Sidebar needs the layout slot; without one (e.g. Blank layout) fall back to floating.
  // lockedSidebar (the investigation exception) docks even when the stored mode isn't sidebar.
  const docked = !isMobile && (isSidebar || lockedSidebar) && slotEl != null;

  // The presentation currently on screen — drives the morph between the launch
  // button and the floating / docked / fullscreen panels. The shell routes force
  // fullscreen from the route itself (not the stored mode), so a prior page's
  // mode-restore can't clobber it during the transition. lockedSidebar wins over a
  // stored fullscreen so the carried chat docks on arrival (never full-covers the
  // investigation) — until an explicit layout pick releases it.
  const presentation = !effectiveOpen ? 'button' : isMobile || lockedFullscreen || isFullscreen && !lockedSidebar ? 'fullscreen' : docked ? 'docked' : 'floating';

  // Publish where the chat is on screen, so the devtools can keep off whichever corner it claims.
  const publishPresentation = useChatWidgetPresentation().publish;
  useEffect(() => {
    publishPresentation({
      presentation,
      position
    });
  }, [presentation, position, publishPresentation]);

  // Fullscreen portals onto the slot that overlays the content card, so the browser
  // keeps it sized to the content area. The slot is always mounted on the Default/Full
  // layouts the chat runs under (it's disabled on blank-layout routes), so this is
  // effectively always true when fullscreen; the fallback to body just avoids a
  // detached host.
  const fullscreenDocked = presentation === 'fullscreen' && fullscreenSlotEl != null;

  // The documents panel + toggle are available in every panel variant (not the
  // collapsed launch button).
  const documentsPanelAvailable = effectiveOpen && presentation !== 'button';

  // Ref (not state): populated synchronously so the morph effect can read it the
  // same tick the button mounts on collapse.
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hostClassName = hostClassNameByPresentation[presentation];
  // Closed: host stays mounted but hidden.
  const hostClosed = !effectiveOpen && presentation === 'button';

  // Left-anchored positions open the overlay off the host's right edge, others off the left — the seam.
  const usesExternalOverlay = presentation === 'docked' || presentation === 'floating';
  const overlayOpen = usesExternalOverlay && documentsPanel.isOpen;
  const overlayAnchorsLeft = position === 'bottom-left' || position === 'top-left';
  // Square the host's seam-side corners while open so panel and chat meet flush.
  const seamSquareClass = overlayOpen ? overlayAnchorsLeft ? 'rounded-r-none' : 'rounded-l-none' : '';

  // Last chat scroll position, restored across host reparents. Cached (not read live)
  // because a reparent can detach the node first, making the live read 0.
  const lastScrollTopRef = useRef(0);

  // Reparent the host (docked slot vs. body) + sync className. Runs before the
  // morph hook so the host is at its new rect when the morph measures.
  useLayoutEffect(() => {
    if (!hostEl) return;
    // Off-route: detach so no empty box lingers.
    if (!enabled) {
      hostEl.remove();
      return;
    }
    const parent = presentation === 'docked' && slotEl ? slotEl : fullscreenDocked ? fullscreenSlotEl : document.body;
    if (hostEl.parentElement !== parent) {
      // Re-attaching a node zeroes its scrollTop, so restore the chat scroller after
      // the move (synchronous here, no flash); else history jumps to top on every
      // presentation change. Fall back to the cached position if the live read is 0.
      const scroller = hostEl.querySelector<HTMLElement>('[data-chat-scroll]');
      const top = Math.max(scroller?.scrollTop ?? 0, lastScrollTopRef.current);
      parent.appendChild(hostEl);
      if (scroller) scroller.scrollTop = top;
    }
    // Floating card anchors to the launch button's corner; --percy-gap (1rem ≙ the
    // old bottom-4/right-4 inset) feeds POSITION_ANCHOR. Other presentations have a
    // fixed anchor of their own, so no per-position class there.
    const floatingAnchor = presentation === 'floating' ? POSITION_ANCHOR[position] : '';
    // Fullscreen needs no per-case top/border: it fills the slot that overlays the
    // content card, which supplies the border + edit-mode color + rounding.
    hostEl.className = cx(hostClassName, floatingAnchor, seamSquareClass, hostClosed && 'hidden');
    if (presentation === 'floating') hostEl.style.setProperty('--percy-gap', '1rem');else hostEl.style.removeProperty('--percy-gap');
    hostEl.setAttribute('role', 'dialog');
    hostEl.setAttribute('data-percy-chat-host', 'true');
    hostEl.setAttribute('aria-label', t($ => $.windowAriaLabel));
  }, [hostEl, enabled, presentation, slotEl, fullscreenDocked, fullscreenSlotEl, hostClassName, hostClosed, seamSquareClass, position, t]);

  // Cache the live scroll position (capture phase — scroll doesn't bubble) so the
  // restore above has a value even when the node was already detached.
  useLayoutEffect(() => {
    if (!hostEl) return;
    const onScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target?.dataset?.chatScroll !== undefined && target.scrollTop > 0) {
        lastScrollTopRef.current = target.scrollTop;
      }
    };
    hostEl.addEventListener('scroll', onScroll, true);
    return () => hostEl.removeEventListener('scroll', onScroll, true);
  }, [hostEl]);

  // Host is appended imperatively, so detach it on unmount ourselves.
  useLayoutEffect(() => () => hostEl?.remove(), [hostEl]);

  // The morph measures the host (or the launch button when closed).
  const getActiveEl = (): HTMLElement | null => presentation === 'button' ? buttonRef.current : hostEl;

  // A phone slides the sheet instead (below): the morph's blank box reads as a flash there.
  const {
    isMorphing,
    isAnimating,
    settling,
    settleMs,
    rect,
    involvesButton,
    onArrived,
    onFaded
  } = useChatWidgetMorph(presentation, getActiveEl, !isMobile);

  // Hidden behind the overlay while moving; fades in on settle. The settled transition also
  // carries top,left so the overlay slides in sync with the nav-collapse grid — an opacity-only
  // transition would override the class and snap the left edge. Dropped during a morph.
  useLayoutEffect(() => {
    if (!hostEl || hostClosed) return;
    hostEl.style.opacity = isAnimating ? '0' : '1';
    hostEl.style.transition = isAnimating ? '' : 'opacity 100ms, top 200ms, left 200ms';
  }, [hostEl, isAnimating, hostClosed]);
  useLayoutEffect(() => {
    if (!hostEl) return;
    if (presentation === 'button') {
      sheetRef.current?.cancel();
      sheetRef.current = null;
      if (sheetClosingRef.current) revealLauncher(buttonRef.current, reduceMotion);
      sheetClosingRef.current = false;
      return;
    }
    if (!sheetEnterRef.current || presentation !== 'fullscreen') return;
    sheetEnterRef.current = false;
    sheetRef.current = slideSheet(hostEl, 'in', reduceMotion, sheetRef.current);
  }, [hostEl, presentation, reduceMotion]);

  // Track the host's rect so the SIDEBAR + FLOATING documents overlays can sit
  // flush to / left of its left edge (fullscreen renders the panel inside the
  // host, no rect math).
  useLayoutEffect(() => {
    if (!usesExternalOverlay || !hostEl) {
      setHostRect(null);
      return;
    }
    // Skip no-op updates so the rAF loop below doesn't re-render every frame.
    let last: DOMRect | null = null;
    const measure = () => {
      const next = hostEl.getBoundingClientRect();
      if (last && last.top === next.top && last.left === next.left && last.right === next.right && last.height === next.height) {
        return;
      }
      last = next;
      setHostRect(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(hostEl);
    window.addEventListener('resize', measure);
    // ResizeObserver misses moves without a resize (scroll, banners, layout settle), so track the host each frame while open.
    let raf = 0;
    const tick = () => {
      measure();
      raf = requestAnimationFrame(tick);
    };
    if (overlayOpen) raf = requestAnimationFrame(tick);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      cancelAnimationFrame(raf);
    };
    // `position` is a dep: re-anchoring moves the fixed host without resizing it, so
    // the ResizeObserver won't fire — re-measure so the documents overlay tracks it.
  }, [usesExternalOverlay, overlayOpen, hostEl, position]);
  if (!enabled) return null;
  const handleLayoutChange = (layout: string) => {
    if (layout !== 'sidebar' && layout !== 'floating' && layout !== 'fullscreen') return;
    switchLayout(layout);
  };

  // Dismisses the chat back to the launch button regardless of the current layout. Releases
  // the investigation dock too — its route-implied open would otherwise hold the panel up.
  const handleCollapse = () => {
    releaseDock();
    if (!isMobile || !hostEl || presentation !== 'fullscreen' || lockedFullscreen) {
      close();
      return;
    }
    if (sheetClosingRef.current) return;
    const exit = slideSheet(hostEl, 'out', reduceMotion, sheetRef.current);
    if (!exit) {
      close();
      return;
    }
    sheetClosingRef.current = true;
    sheetRef.current = exit;
    exit.onfinish = close;
  };
  const handleOpen = () => {
    sheetEnterRef.current = isMobile;
    open();
  };
  const CollapseIcon = isFullscreen || docked ? IcoChevronRightDouble16 : IcoMinusSmall16;

  // While the dock is engaged the stored mode can disagree with what's on screen — surface
  // the docked reality in the trigger icon and the dropdown's selected radio.
  const effectiveLayout = lockedSidebar ? 'sidebar' : mode;
  const triggerIcon = effectiveLayout === 'sidebar' ? <IcoSidebar16 /> : effectiveLayout === 'fullscreen' ? <IcoFullscreen16 /> : <IcoFloating16 />;
  const documentsToggle = <button ref={documentsToggleRef} type="button"
  // Fold the count into the name — an explicit aria-label suppresses the badge's text,
  // so without this a screen reader never hears how many resources there are. (Plural
  // forms are selected explicitly, matching the typed-i18n pattern elsewhere.)
  aria-label={items.length === 0 ? t($_0 => $_0.documentsPanel.ariaLabel) : items.length === 1 ? t($_1 => $_1.documentsPanel.ariaLabelWithCount_one, {
    count: 1
  }) : t($_2 => $_2.documentsPanel.ariaLabelWithCount_other, {
    count: items.length
  })} aria-pressed={resourcesOpen} onClick={() => resourcesOpen ? documentsPanel.close() : documentsPanel.openTo('resources')} className={cx(iconButtonClass, 'relative', resourcesOpen && 'bg-senary/70')}>
      <span className="flex items-center justify-center" style={items.length > 0 ? badgeCutout : undefined}>
        <IcoFolders16 />
      </span>
      {items.length > 0 && <span ref={badgeRef} aria-hidden="true" className="absolute -top-1 -right-1 flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-accent text-quinary text-[11px] leading-none font-medium tabular-nums">
          {items.length}
        </span>}
    </button>;

  // On the Percy home (a full-screen chat with no session yet) the header floats with no fill or
  // rule, so the backdrop runs up to the top edge.
  const headerFloats = presentation === 'fullscreen' && !activeChatSessionId;

  // The header chrome, reused by the live frame and the morph placeholder.
  const header = <div className={cx('flex items-center justify-between gap-2 px-2 md:px-3 h-12 md:h-11 shrink-0', headerFloats ? 'absolute inset-x-0 top-0 z-1' : 'border-b border-tertiary bg-primary')}>
      {/* The Percy page owns its fullscreen and keeps the menu; over any other page the chat is a
          layer the user opened, so that corner closes it. */}
      {isMobile && (lockedFullscreen ? <IconButton icon={IcoSidebarLeftClosed16} aria-label={tCommon($_3 => $_3.components.privateLayout.nav.mobileNavigation.open)} onClick={() => setNavigationOpen(true)} className="size-11 shrink-0" /> : <IconButton icon={IcoClose16} aria-label={t($_4 => $_4.close)} onClick={handleCollapse} className="size-11 shrink-0" />)}
      {/* Documents panel toggle — available in every panel variant. On mobile it moves to the
          right edge, next to where its panel slides in. */}
      {documentsPanelAvailable && !isMobile && <div className="flex items-center gap-2 shrink-0">
          {documentsToggle}
          {/* Over the home's backdrop a fixed grey can match the wave behind it, while a tint of the
              text colour stays visible on all of its colours. */}
          <span aria-hidden="true" className={cx('w-px h-4', headerFloats ? 'text-primary bg-current/12' : 'bg-(--border-color-tertiary)')} />
        </div>}
      {/* min-w-0 wrapper lets a long title truncate instead of pushing the buttons out.
          The trigger is content-width (max-w-full) so the chevron hugs the title — it
          reads as "this title is a dropdown", not as another right-side control. On a phone
          the title sits as close to the corner button as the app top bar's does. */}
      <div className="flex-1 min-w-0 max-md:-ml-2">
        <ChatHistoryDropdown activeSessionId={sessionId} onSelectSession={adoptSession} onNewChat={handleNewChat} className="inline-flex items-center gap-1 h-11 md:h-7 pl-3 max-md:pl-0 pr-2 max-w-full rounded-(--button-border-radius) hover:bg-hover data-[state=open]:bg-senary/70 cursor-pointer min-w-0 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary" />
      </div>
      <div className="flex items-center gap-1 md:gap-2 shrink-0">
        <NotificationsHeaderButton className={iconButtonClass} />
        {activeChatSessionId || !lockedFullscreen ? <>
            {activeChatSessionId && <ChatWidgetShareControl chatSessionId={activeChatSessionId} userId={user?.id} />}
            {!lockedFullscreen && <>
                <Dropdown.Root modal={false}>
                  <Dropdown.Trigger asChild>
                    <button type="button" aria-label={t($_5 => $_5.layoutSwitcher.ariaLabel)} className={cx(iconButtonClass, 'max-md:hidden')}>
                      {triggerIcon}
                    </button>
                  </Dropdown.Trigger>
                  <Dropdown.Portal>
                    <Dropdown.Content align="end" sideOffset={4} className="min-w-[180px]">
                      <Dropdown.RadioGroup value={effectiveLayout} onValueChange={handleLayoutChange}>
                        <Dropdown.Item variant="radio" value="sidebar" iconLeft={IcoSidebar16}>
                          {t($_6 => $_6.layoutSwitcher.sidebar)}
                        </Dropdown.Item>
                        <Dropdown.Item variant="radio" value="floating" iconLeft={IcoFloating16}>
                          {t($_7 => $_7.layoutSwitcher.floating)}
                        </Dropdown.Item>
                        <Dropdown.Item variant="radio" value="fullscreen" iconLeft={IcoFullscreen16}>
                          {t($_8 => $_8.layoutSwitcher.fullscreen)}
                        </Dropdown.Item>
                      </Dropdown.RadioGroup>
                    </Dropdown.Content>
                  </Dropdown.Portal>
                </Dropdown.Root>
                <button type="button" aria-label={t($_9 => $_9.collapse)} onClick={handleCollapse} className={cx(iconButtonClass, 'max-md:hidden')}>
                  <CollapseIcon />
                </button>
              </>}
          </> : null}
        {documentsPanelAvailable && isMobile && documentsToggle}
      </div>
    </div>;

  // Panel content, reused by every variant's wrapper below. One lateral slot with two
  // modes (not tabs), each with its own contextual header: the Resources view (Context Explorer,
  // opened by the folder toggle) or the Background-agents view (opened by the composer indicator).
  // Fixed-width so it doesn't reflow while a desktop wrapper animates its width; the phone's drawer
  // slides instead.
  const documentsPanelEl = <div className="h-full" style={isMobile ? undefined : {
    width: DOCUMENTS_PANEL_WIDTH
  }}>
      {documentsPanel.tab === 'agents' ? <BackgroundAgentsPanel onClose={documentsPanel.close} /> : <ContextExplorer items={items}
    // Expand folders by default so the chat's resources are visible at a glance; each stays
    // collapsible.
    defaultOpen
    // The resource the current route is on — highlighted + its folder opened.
    activeId={activeResourceId} onClose={documentsPanel.close} onResourceSelected={(resource, event) => {
      // presentResource resolves the right destination per kind (builder, investigation,
      // entity, …) and carries the active chat via ?chat=, mirroring the resource-created
      // cards. Resolve the chat from the current route so a sidebar selection preserves it
      // (and survives a reload).
      const chatSessionId = resolveNavigationChatSessionId({
        fallbackChatSessionId: activeChatSessionId,
        pathname: router.pathname,
        query: router.query
      });
      const presentation_0 = presentResource(resource, tCopilot, {
        chatSessionId
      });
      if (!presentation_0) return;
      // Cmd/Ctrl-click opens a new tab (native-anchor behavior, cf. useOpenPage in the users
      // list); a plain click navigates in place. The href threads the active chat via ?chat=
      // for builder/investigation destinations, mirroring the resource-created cards.
      if (event.metaKey || event.ctrlKey) {
        window.open(presentation_0.href, '_blank');
        return;
      }
      void router.push(presentation_0.href);
    }} />}
    </div>;

  // SIDEBAR + FLOATING: a fixed element right-anchored to the host's left edge
  // that opens OUTWARD to the left (width 0 ↔ DOCUMENTS_PANEL_WIDTH), so the
  // chat/card never changes width. The panel spans the full host height and rounds
  // its outer corners (the inner edge is the card seam) to match the card it grows
  // out of.
  const documentsOverlay = usesExternalOverlay && hostRect && <div className={cx('fixed z-20 overflow-hidden transition-[width] duration-300 ease-in-out',
  // Border only while open (at width 0 it'd double the chat's border); no shadow — it opens flush, so one would bleed over the seam.
  !documentsPanel.isOpen && 'pointer-events-none', documentsPanel.isOpen && (overlayAnchorsLeft ? 'border-r border-y border-tertiary rounded-tr-2xl rounded-br-2xl' : 'border-l border-y border-tertiary rounded-tl-2xl rounded-bl-2xl'))} style={{
    top: hostRect.top,
    height: hostRect.height,
    ...(overlayAnchorsLeft ? {
      left: hostRect.right
    } : {
      right: window.innerWidth - hostRect.left
    }),
    width: documentsPanel.isOpen ? DOCUMENTS_PANEL_WIDTH : 0
  }} aria-hidden={!documentsPanel.isOpen} inert={!documentsPanel.isOpen}>
      {documentsPanelEl}
    </div>;

  // FULLSCREEN: on a desktop the panel slides in from the left and overlays the chat INSIDE the host
  // (chat keeps full width underneath). Absolute within the host, clipped by the host's
  // overflow-hidden, so it spans the same height as the chat frame.
  const insetDocumentsPanel = presentation === 'fullscreen' && (isMobile ? <ChatWidgetDocumentsDrawer open={documentsPanel.isOpen} onClose={documentsPanel.close}>
        {documentsPanelEl}
      </ChatWidgetDocumentsDrawer> : <div className={cx('absolute inset-y-0 left-0 z-10 overflow-hidden bg-primary transition-[width] duration-300 ease-in-out', documentsPanel.isOpen ? 'shadow-lg border-r border-tertiary' : 'pointer-events-none')} style={{
    width: documentsPanel.isOpen ? DOCUMENTS_PANEL_WIDTH : 0
  }} aria-hidden={!documentsPanel.isOpen} inert={!documentsPanel.isOpen}>
        {documentsPanelEl}
      </div>);

  // The chat lives in the persistent host; it stays mounted once a session exists
  // (or while open) so open/close and layout switches preserve its state.
  const frame = (effectiveOpen || sessionId) && <>
      {insetDocumentsPanel}
      {header}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {/* key remounts on session change: resets per-session latches and the draft composer. */}
        <ChatWidgetBody key={sessionId ?? 'draft'} sessionId={sessionId} isAssumingRouteTenant={isAssumingRouteTenant}
      // 'button' (closed) shares the floating box; the body is hidden then, so map it to floating.
      presentation={presentation === 'button' ? 'floating' : presentation} onSessionCreated={adoptSession} onTurnComplete={handleTurnComplete} onApprovalRequired={handleApprovalRequired} />
      </div>
    </>;

  // Hide the launch button behind the overlay while morphing to/from it.
  const buttonFade = isAnimating ? 'opacity-0' : 'opacity-100 transition-opacity duration-100';
  const buttonVisibility = rightDrawer.isCovering ? 'opacity-0 pointer-events-none' : buttonFade;
  return <>
      {!effectiveOpen && !navigationOpen && <OpenChatButton ref={buttonRef} onClick={handleOpen} title={t($_10 => $_10.openButton)} className={isMobile && !rightDrawer.isCovering ? undefined : buttonVisibility} draggable={!isMobile} position={position} onPositionChange={setPosition} rightInset={rightDrawer.isCovering ? 0 : rightDrawer.inset} />}
      {hostEl && createPortal(frame, hostEl)}
      {documentsOverlay}
      {isMorphing && rect && <ChatWidgetMorphLayer rect={rect} settling={settling} settleMs={settleMs} onArrived={onArrived} onFaded={onFaded}>
          {/* Button morphs use a plain box; panel↔panel keeps the header. The fullscreen
              assume banner lives in the layout slot (not the host), so the mirror omits it. */}
          {!involvesButton && header}
          <div className="flex-1 min-h-0" />
        </ChatWidgetMorphLayer>}
    </>;
};
export default ChatWidget;
function _temp() {
  if (typeof document === "undefined") {
    return null;
  }
  return document.createElement("div");
}
function _temp2(prev) {
  return prev;
}